import Link from 'next/link'
import styled from 'styled-components'
import { InlineTicks, ticksLabel } from 'views/Social/PersonTicks'
import { CopyButton, ScanLink } from '../components/AddressActions'
import { MONO } from '../ui'
import { ProfileState, WalletProfile, checksum, cleanAvatar, cleanName, shortChecksum } from './logic'

/**
 * Who a wallet is, on the leaderboard. One cell, always the same shape (avatar slot, the words, two small actions), so a profile arriving late
 * changes the words and the picture, never the layout. The avatar and the words are ONE link to the wallet's CADINU profile, /u/<address>
 * (Bez 2026-10-04); copy and BscScan stay beside it as their own targets:
 *   profile on   the NFT avatar, the /u handle as the name and the short address as a smaller second line (Bez 2026-10-04)
 *   profile off  muted placeholder (struck through), the short checksummed address and a "Profile off" tag; the name is never shown
 *   no profile   muted placeholder and the short checksummed address
 *   not known    the same as no profile, without anything said about it (the lookup is late or failed)
 */
const SIZE = 40
const SIZE_SMALL = 32

const Wrap = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 4px;
  min-width: 0;
`
/** avatar + words: one target to the profile. Row: avatar left of the words; stack (podium): avatar over centred words. */
const ProfileLink = styled(Link)`
  display: grid;
  grid-template-columns: ${SIZE_SMALL}px minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 44px;
  border-radius: 14px;
  color: inherit;
  text-decoration: none;
  ${({ theme }) => theme.mediaQueries.sm} {
    grid-template-columns: ${SIZE}px minmax(0, 1fr);
    gap: 10px;
  }
  [data-layout='stack'] > & {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    max-width: 100%;
  }
  &:hover [data-testid='v3-name'],
  &:hover [data-testid='v3-addr'][data-primary='true'] {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  &:hover [data-testid='v3-avatar'] {
    filter: brightness(1.12);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }
`
const Avatar = styled.span<{ $muted: boolean; $size?: number; $ring?: string }>`
  position: relative;
  display: inline-flex;
  width: ${({ $size }) => $size ?? SIZE_SMALL}px;
  height: ${({ $size }) => $size ?? SIZE_SMALL}px;
  border-radius: 50%;
  overflow: hidden;
  flex: none;
  color: ${({ theme }) => theme.colors.textSubtle};
  opacity: ${({ $muted }) => ($muted ? 0.75 : 1)};
  box-shadow: ${({ $muted, $ring }) => ($ring ? `0 0 0 3px ${$ring}, 0 0 22px ${$ring}` : $muted ? 'none' : '0 0 0 2px rgba(127, 212, 232, 0.55), 0 0 14px rgba(127, 212, 232, 0.3)')};
  ${({ theme, $size }) => ($size ? '' : `${theme.mediaQueries.sm} { width: ${SIZE}px; height: ${SIZE}px; }`)}
  img,
  svg {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }
`
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  text-align: center;
  /* a podium slot on a phone is ~100 px: the team chip keeps its shield, its words move into the tooltip and the link's name */
  @media (max-width: 575px) {
    [data-testid='pt-team'] {
      padding: 0 3px;
    }
    .chip-words {
      display: none;
    }
    /* a chip without its own shield (the gold official one is the profile's shield) would be an empty pill here */
    [data-testid='pt-team'][data-shield='0'] {
      display: none;
    }
  }
`
const Text = styled.span<{ $center?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  gap: 0;
  min-width: 0;
  max-width: 100%;
`
const Line = styled.span<{ $center?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px 8px;
  min-width: 0;
  max-width: 100%;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
`
const Name = styled.span`
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 800;
  font-size: 15px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.text};
`
const AddrText = styled.span<{ $muted: boolean; $secondary?: boolean }>`
  font-family: ${MONO};
  font-size: ${({ $secondary }) => ($secondary ? 12 : 13)}px;
  [data-layout='stack'] & {
    /* the podium: never under 12 px (review-ui: three 11 px addresses), the primary/secondary ratio kept */
    font-size: ${({ $secondary }) => ($secondary ? 12 : 13)}px;
  }
  line-height: ${({ $secondary }) => ($secondary ? 16 : 20)}px;
  white-space: nowrap;
  color: ${({ theme, $muted, $secondary }) => ($muted || $secondary ? theme.colors.textSubtle : theme.colors.text)};
`
const OffTag = styled.span`
  [data-layout='stack'] & {
    font-size: 12px;
    padding: 0 6px;
    letter-spacing: 0.04em;
  }
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  border: 1px dashed ${({ theme }) => theme.colors.textSubtle};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.textSubtle};
`
const Actions = styled.span`
  display: inline-flex;
  align-items: center;
  /* two 44 px targets side by side; the row's own height is not stretched by them */
  gap: 0;
`

function Placeholder({ off }: { off: boolean }) {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true" focusable="false">
      <circle cx="20" cy="20" r="19" fill="none" stroke="currentColor" strokeOpacity=".4" strokeWidth="1.5" strokeDasharray="3 3" />
      <circle cx="20" cy="16" r="5.5" fill="currentColor" fillOpacity=".3" />
      <path d="M9.5 32c1.6-5 6-7.5 10.5-7.5S28.9 27 30.5 32" fill="currentColor" fillOpacity=".3" />
      {off && <path d="M9 31 31 9" stroke="currentColor" strokeOpacity=".7" strokeWidth="2.2" strokeLinecap="round" />}
    </svg>
  )
}

export default function WalletIdentity({ wallet, profile, stack, size, ring }: { wallet: string; profile?: WalletProfile; /** podium layout: big avatar over centred text */ stack?: boolean; size?: number; ring?: string }) {
  const full = checksum(wallet)
  const state: ProfileState | 'unknown' = profile?.state ?? 'unknown'
  const name = state === 'on' ? cleanName(profile?.name) : undefined // off or missing never shows a name, and a placeholder is no name
  const picture = state === 'on' ? cleanAvatar(profile?.avatar) : undefined
  const hasPicture = Boolean(picture)
  const muted = !hasPicture
  const Outer = stack ? Stack : Wrap
  const shortAddr = shortChecksum(wallet)
  // the gold official and blue token-team ticks ride beside the name (pictures inside the one profile link; their meaning joins its name)
  const official = state === 'on' && profile?.official === true
  const teams = state === 'on' ? profile?.teams ?? [] : []
  const ticks = official || teams.length ? <InlineTicks official={official} teams={teams} size={20} testId="v3-ticks" /> : null
  const tickWords = ticks ? `, ${ticksLabel(official, teams)}` : ''
  return (
    <Outer data-testid="v3-who" data-state={state} data-wallet={wallet} data-layout={stack ? 'stack' : 'row'}>
      <ProfileLink href={`/u/${full}`} data-testid="v3-who-link" aria-label={`${name ?? shortAddr}${tickWords}: open CADINU profile`} title={name ? `${name} · ${full}` : full}>
        <Avatar $muted={muted} $size={size} $ring={ring} data-testid="v3-avatar" data-kind={hasPicture ? 'nft' : state === 'off' ? 'off' : 'placeholder'}>
          {hasPicture ? (
            <img src={picture} alt="" width={SIZE} height={SIZE} loading="lazy" decoding="async" />
          ) : (
            <Placeholder off={state === 'off'} />
          )}
        </Avatar>
        <Text $center={stack}>
          {name && (
            <Line $center={stack}>
              <Name data-testid="v3-name">{name}</Name>
              {ticks}
            </Line>
          )}
          <Line $center={stack}>
            <AddrText $muted={state === 'off'} $secondary={Boolean(name)} data-primary={name ? 'false' : 'true'} data-testid="v3-addr">{shortAddr}</AddrText>
            {!name && ticks}
            {state === 'off' && <OffTag data-testid="v3-profile-off">Profile off</OffTag>}
          </Line>
        </Text>
      </ProfileLink>
      <Actions>
        <CopyButton value={full} label="address" testId="v3-who-copy" />
        <ScanLink value={full} label="address" testId="v3-who-scan" />
      </Actions>
    </Outer>
  )
}
